/**
 * KPICard Component
 *
 * Displays key performance indicators with:
 * - Fixed-height zones for consistent alignment
 * - Label zone: min 36px (bottom-aligned)
 * - Value zone: 28px bold font
 * - Change zone: min 32px (top-aligned) with trend indicator
 * - Responsive grid support
 * - Optional click navigation (MVP 1.21.3 - SCRUM-02)
 *
 * Epic: MVP 1.21.2 - Dashboard Design System & Notifications (SCRUM-459)
 * Component: KPI Card UI Component
 * Enhanced: MVP 1.21.3 - KPI Card Navigation (SCRUM-02)
 *
 * @component
 * @param {Object} props
 * @param {string} props.label - KPI label/description
 * @param {string|number} props.value - KPI value to display
 * @param {Object} props.change - Change data { value, trend: 'up'|'down'|'neutral' }
 * @param {React.ElementType} props.icon - Icon component from lucide-react
 * @param {string} props.color - Accent color (default: 'primary')
 * @param {boolean} props.loading - Show loading state
 * @param {Function} props.onClick - Click handler for navigation (makes card clickable)
 * @param {string} props.href - Optional href for accessibility
 */

import React from 'react';
import PropTypes from 'prop-types';
import { TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';

const KPICard = ({
  label,
  value,
  change,
  icon: Icon,
  color = 'primary',
  loading = false,
  onClick,
  href: _href
}) => {
  // Get color variable
  const getColor = () => {
    switch (color) {
      case 'primary':
        return 'var(--color-primary)';
      case 'success':
        return 'var(--color-success)';
      case 'warning':
        return 'var(--color-warning)';
      case 'error':
        return 'var(--color-error)';
      case 'info':
        return 'var(--color-info)';
      default:
        return 'var(--color-primary)';
    }
  };

  // Get trend color and icon
  const getTrendDetails = () => {
    if (!change) {return null;}

    switch (change.trend) {
      case 'up':
        return {
          color: 'var(--color-success)',
          icon: TrendingUp,
          text: '↑',
        };
      case 'down':
        return {
          color: 'var(--color-error)',
          icon: TrendingDown,
          text: '↓',
        };
      case 'neutral':
        return {
          color: 'var(--color-text-secondary)',
          icon: Minus,
          text: '−',
        };
      default:
        return {
          color: 'var(--color-text-secondary)',
          icon: Minus,
          text: '',
        };
    }
  };

  const trendDetails = getTrendDetails();
  const isClickable = Boolean(onClick);

  // Build card style with clickable states
  const cardStyle = {
    ...styles.card,
    cursor: isClickable ? 'pointer' : 'default',
    ...(isClickable && {
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: 'var(--shadow-md)',
      },
    }),
  };

  const handleClick = (  _e) => {
    if (onClick) {
      _e.preventDefault();
      onClick();
    }
  };

  const handleKeyDown = (  _e) => {
    if (isClickable && (_e.key === 'Enter' || _e.key === ' ')) {
      _e.preventDefault();
      onClick();
    }
  };

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner} />
        </div>
      </div>
    );
  }

  const CardWrapper = isClickable ? 'button' : 'div';
  const wrapperProps = isClickable
    ? {
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        'aria-label': `View details for ${label}`,
        role: 'button',
        tabIndex: 0,
        style: { ...cardStyle, border: 'none', textAlign: 'left', width: '100%' },
      }
    : { style: cardStyle };

  return (
    <CardWrapper {...wrapperProps}>
      <div style={styles.cardInner}>
        {/* Label Zone - min 36px, bottom-aligned */}
        <div style={styles.labelZone}>
          {Icon && (
            <Icon
              size={20}
              style={{ color: getColor(), marginRight: 'var(--space-2)' }}
            />
          )}
          <span style={styles.label}>{label}</span>
          {isClickable && (
            <ArrowRight
              size={16}
              style={{
                color: 'var(--color-text-tertiary)',
                marginLeft: 'auto',
                transition: 'transform var(--transition-base)',
              }}
              className="kpi-card-arrow"
            />
          )}
        </div>

        {/* Value Zone - 28px bold */}
        <div style={styles.valueZone}>
          <span style={styles.value}>{value}</span>
        </div>

        {/* Change Zone - min 32px, top-aligned */}
        {change && trendDetails && (
          <div style={styles.changeZone}>
            <span
              style={{
                ...styles.changeText,
                color: trendDetails.color,
              }}
            >
              {trendDetails.text} {change.value}
            </span>
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

const styles = {
  card: {
    backgroundColor: 'var(--color-white)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-5)',
    minHeight: 'var(--kpi-card-min-height)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'all var(--transition-base)',
    cursor: 'default',
  },

  cardInner: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 'var(--kpi-card-min-height)',
  },

  labelZone: {
    display: 'flex',
    alignItems: 'flex-end',
    minHeight: 'var(--kpi-card-label-min-height)',
    marginBottom: 'var(--space-2)',
  },

  label: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 'var(--line-height-tight)',
  },

  valueZone: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 'var(--space-2)',
  },

  value: {
    fontSize: 'var(--kpi-card-value-font-size)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    lineHeight: 1,
  },

  changeZone: {
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: 'var(--kpi-card-change-min-height)',
    marginTop: 'auto', // Push to bottom
  },

  changeText: {
    fontSize: 'var(--kpi-card-change-font-size)',
    fontWeight: 'var(--font-weight-medium)',
    lineHeight: 'var(--line-height-tight)',
  },

  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'var(--kpi-card-min-height)',
  },

  loadingSpinner: {
    width: '24px',
    height: '24px',
    border: '2px solid var(--color-border)',
    borderTopColor: 'var(--color-primary)',
    borderRadius: 'var(--radius-full)',
    animation: 'spin 0.8s linear infinite',
  },
};

// Add spin animation keyframes and hover effects
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    /* Hover effects for clickable KPI cards (MVP 1.21.3 - SCRUM-02) */
    button[role="button"]:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }

    button[role="button"]:hover .kpi-card-arrow {
      transform: translateX(4px);
    }

    button[role="button"]:focus {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    button[role="button"]:active {
      transform: translateY(0);
    }
  `;
  document.head.appendChild(styleSheet);
}

KPICard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  change: PropTypes.shape({
    value: PropTypes.string,
    trend: PropTypes.oneOf(['up', 'down', 'neutral']),
  }),
  icon: PropTypes.elementType,
  color: PropTypes.oneOf(['primary', 'success', 'warning', 'error', 'info']),
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  href: PropTypes.string,
};

export default KPICard;
